// Passthrough tabletop (2026-09-14). On a Quest 3 (or a phone with ARCore), the hole becomes a
// diorama on the real table: an `immersive-ar` session, a hit-test reticle on the surface, one
// pinch or trigger to put the course down, and the same swing as VR — the gesture is the same
// size whatever the course's scale, so the physics sees nothing different. The sky and the fog
// go away (the room is the sky), the stage shrinks to 1:160, and the player stands where they
// are: `standAt` never runs in AR. The Puzzle Table, on the table.
//
// What this is not: it does not occlude the course behind real objects (no depth sensing), and
// it has not been seen through a headset; scripts/xr-witness.mjs proves the session, the
// placement and the shot with a fake device.

import * as THREE from 'three';
import { ARButton } from 'three/addons/webxr/ARButton.js';
import type { World } from './scene/world';

export interface AR {
  /** an immersive-ar session is presenting */
  active(): boolean;
  /** the course has been put down on a surface */
  placed(): boolean;
  /** milliseconds since it was put down (Infinity if never): the placing press is not a swing */
  placedAgoMs(): number;
  /** the stage's yaw, so a world-space swing direction can be read in course coordinates */
  stageYawDeg(): number;
  /** call every frame with the XR frame (or nothing) */
  update(): void;
}

export interface AROptions {
  scale?: number;
  onSessionChange?(active: boolean): void;
  onPlaced?(): void;
}

export function setupAR(world: World, stage: THREE.Group, options: AROptions = {}): AR {
  const { renderer, scene, rig } = world;
  const SCALE = options.scale ?? 1 / 160;
  let arSession: XRSession | null = null;
  let isPlaced = false;
  let hitSource: XRHitTestSource | null = null;
  let hitRequested = false;
  const saved = { fog: null as THREE.Fog | THREE.FogExp2 | null, skyVisible: true, scale: 1, pos: new THREE.Vector3(), rotY: 0 };

  // the reticle: a thin ring that sits on whatever surface the headset finds
  const reticle = new THREE.Mesh(
    new THREE.RingGeometry(0.05, 0.065, 40),
    new THREE.MeshBasicMaterial({ color: 0xe0b13c, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false }),
  );
  reticle.name = 'ar-reticle';
  reticle.rotation.x = -Math.PI / 2;
  reticle.matrixAutoUpdate = false;
  reticle.visible = false;
  scene.add(reticle);

  const xrApi = (navigator as Navigator & { xr?: { isSessionSupported(m: string): Promise<boolean> } }).xr;
  if (xrApi) {
    xrApi.isSessionSupported('immersive-ar').then((ok) => {
      if (!ok) return;
      const hud = document.getElementById('hud');
      const button = ARButton.createButton(renderer, {
        requiredFeatures: ['hit-test'],
        optionalFeatures: ['local-floor', 'hand-tracking', 'dom-overlay'],
        ...(hud ? { domOverlay: { root: hud } } : {}),
      });
      button.textContent = 'ON THE TABLE (AR)';
      button.style.bottom = '72px';
      document.body.appendChild(button);
    }, () => {});
  }

  function enter(session: XRSession) {
    arSession = session;
    isPlaced = false;
    saved.fog = scene.fog as THREE.Fog | null;
    saved.skyVisible = world.sky.visible;
    saved.scale = stage.scale.x;
    saved.pos.copy(stage.position);
    saved.rotY = stage.rotation.y;
    scene.fog = null;
    world.sky.visible = false;
    renderer.setClearColor(0x000000, 0);
    stage.scale.setScalar(SCALE);
    stage.visible = false;          // until it is put down
    rig.position.set(0, 0, 0);
    rig.rotation.set(0, 0, 0);
    hitRequested = false;
    options.onSessionChange?.(true);
  }
  function leave() {
    arSession = null;
    hitSource?.cancel?.();
    hitSource = null;
    reticle.visible = false;
    scene.fog = saved.fog;
    world.sky.visible = saved.skyVisible;
    renderer.setClearColor(0x000000, 1);
    stage.scale.setScalar(saved.scale);
    stage.position.copy(saved.pos);
    stage.rotation.y = saved.rotY;
    stage.visible = true;
    isPlaced = false;
    options.onSessionChange?.(false);
  }

  renderer.xr.addEventListener('sessionstart', () => {
    const s = renderer.xr.getSession();
    if (s && s.environmentBlendMode !== 'opaque') enter(s);
  });
  renderer.xr.addEventListener('sessionend', () => { if (arSession) leave(); });

  // a trigger or a pinch while the reticle shows puts the course down, facing the player
  const place = () => {
    if (!arSession || isPlaced || !reticle.visible) return;
    const p = new THREE.Vector3().setFromMatrixPosition(reticle.matrix);
    stage.position.copy(p);
    const cam = renderer.xr.getCamera();
    const toPlayer = Math.atan2(cam.position.x - p.x, cam.position.z - p.z);
    stage.rotation.y = toPlayer + Math.PI; // the tee nearest the player, the flag away from them
    stage.visible = true;
    reticle.visible = false;
    isPlaced = true;
    placedAt = performance.now();
    options.onPlaced?.();
  };
  let placedAt = -Infinity;
  for (const i of [0, 1]) {
    renderer.xr.getController(i).addEventListener('select', place);
    renderer.xr.getHand(i).addEventListener('pinchend', place);
  }

  return {
    active: () => arSession !== null,
    placed: () => isPlaced,
    placedAgoMs: () => (isPlaced ? performance.now() - placedAt : Infinity),
    stageYawDeg: () => (stage.rotation.y * 180) / Math.PI,
    update() {
      if (!arSession || isPlaced) return;
      const frame = renderer.xr.getFrame();
      const ref = renderer.xr.getReferenceSpace();
      if (!frame || !ref) return;
      if (!hitSource && !hitRequested) {
        hitRequested = true;
        arSession.requestReferenceSpace('viewer').then((viewer) => arSession?.requestHitTestSource?.({ space: viewer })).then((src) => { hitSource = src ?? null; }, () => {});
      }
      if (!hitSource) return;
      const hits = frame.getHitTestResults(hitSource);
      if (hits.length) {
        const pose = hits[0].getPose(ref);
        if (pose) { reticle.visible = true; reticle.matrix.fromArray(pose.transform.matrix); reticle.matrix.multiply(new THREE.Matrix4().makeRotationX(-Math.PI / 2)); }
      } else {
        reticle.visible = false;
      }
    },
  };
}
